import React, {useEffect, useState} from 'react';
import {Link as RouterLink} from 'react-router-dom';

// MUI
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import LinearProgress from '@mui/material/LinearProgress';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormHelperText from '@mui/material/FormHelperText';
import Collapse from '@mui/material/Collapse';

// Hooks personalizados y utilidades
import {hooks} from 'lib.front';
import {transformacion} from 'lib.common';
import * as keys from 'src/config/keys';

// Styles
import {makeStyles} from 'tss-react/mui';

// Tipos
import {RegistroResponse, QueryParams, EsquemaCampos} from './types';
import {PRODUCTOS_DISPONIBLES_SIGNUP} from 'src/constants/productos';
import {PlanSimple, ValidacionCampo} from 'src/features/core/types/miscTypes';
import {SCRN_DS_LOG_IN} from 'src/Routes/screens';
import DSSignUpExito from './DSSignUpExito';

// Tipos para el hook useValidacion

interface EsquemaValidacion {
   email: ValidacionCampo;
   plan: ValidacionCampo;
}

// Props del componente
interface DSSignUpProps {}

// planes disponibles

// Esquemas de validación
const esquemaCampos: EsquemaCampos = {
   email: {
      valor:
         process.env.NODE_ENV !== 'production' || process.env.REACT_APP_DS_ENV !== 'PROD'
            ? 'test@dscont.pe'
            : '',
      error: ''
   },
   plan: {
      valor: '',
      error: ''
   }
};

const esquemaValidacion: EsquemaValidacion = {
   email: {
      requerido: true,
      validador: {
         regla: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
         error: 'El correo electronico no tiene un formato valido: [nombre]@[dominio].[etc]'
      }
   },
   plan: {
      requerido: true
   }
};

const useStyles = makeStyles()(theme => ({
   signUpButton: {
      boxShadow:
         '0 1px 1px 0 rgba(0,0,0,0.14), 0 2px 1px -1px rgba(0,0,0,0.12), 0 1px 3px 0 rgba(0,0,0,0.20)',
      backgroundColor: '#FFFFFF',
      color: 'black',
      '&:hover': {
         backgroundColor: '#d5d5d5'
      },
      padding: theme.spacing(1, 3)
   },
   actionButtons: {
      marginTop: theme.spacing(3),
      '& > *': {
         margin: theme.spacing(1)
      }
   },
   successIcon: {
      fontSize: 64,
      color: theme.palette.success.main,
      marginBottom: theme.spacing(2)
   }
}));

function DSSignUp({}: DSSignUpProps): React.ReactElement {
   const {classes} = useStyles();

   // Tipar el hook de validación
   const {campos, handleOnChange, validarTodos, setCampo} = hooks.useValidacion(
      esquemaCampos,
      esquemaValidacion,
      () => {}
   );

   const [planes, setPlanes] = useState<PlanSimple[]>([]);
   const [loadingPlanes, setLoadingPlanes] = useState<boolean>(false);
   const [errPlanesMsg, setErrPlanesMsg] = useState<string>('');

   async function handleGetPlans() {
      setLoadingPlanes(true);
      setErrPlanesMsg('');
      try {
         const planesUri = transformacion.appendUrl(keys.urls.backEnd, '/key/planes');
         const res = await fetch(planesUri, {
            mode: 'cors',
            credentials: 'include',
            headers: {
               'Content-Type': 'application/json'
            }
         });

         if (!res.ok) {
            throw new Error(`${res.status} - ${res.statusText}`);
         }

         const responseJSON: {data: PlanSimple[]} = await res.json();
         if (!responseJSON || !responseJSON.data || responseJSON.data.length === 0) {
            throw new Error('No se encontraron planes activos, intentelo mas tarde.');
         }

         const nuevaListaPlanes: PlanSimple[] = responseJSON.data.filter(currPlan =>
            Boolean(
               PRODUCTOS_DISPONIBLES_SIGNUP.find(
                  currProd => currProd.codProducto === currPlan.scopeProd
               )
            )
         );
         if (nuevaListaPlanes.length === 0) {
            throw new Error('No se encontraron planes activos con productos validos.');
         }
         setPlanes(nuevaListaPlanes);
         setCampo('plan', nuevaListaPlanes[0].idPlan);
      } catch (err) {
         console.error('Error en planes:', err);
         setErrPlanesMsg(err instanceof Error ? err.message : 'Error al recuperar planes');
      } finally {
         setLoadingPlanes(false);
      }
   }
   useEffect(() => {
      handleGetPlans();
   }, []);

   const [errMsg, setErrMsg] = useState<string>('');
   const [loadingRegistro, setLoadingRegistro] = useState<boolean>(false);
   const [registroExitoso, setRegistroExitoso] = useState<boolean>(false);

   async function handleRegistro(): Promise<void> {
      setLoadingRegistro(true);
      setErrMsg('');

      const esOk = await validarTodos();
      if (!esOk) {
         setLoadingRegistro(false);
      }

      try {
         const body = {
            email: campos.email.valor.trim(),
            idPlan: campos.plan.valor
         };

         const registroUri = transformacion.appendUrl(
            keys.urls.backEnd,
            '/key/suscripcion/plan-perpetuo'
         );

         const res = await fetch(registroUri, {
            method: 'POST',
            mode: 'cors',
            credentials: 'include',
            headers: {
               'Content-Type': 'application/json'
            },
            body: JSON.stringify(body)
         });

         if (!res.ok) {
            const data = await res.json();
            throw data;
         }

         const data: RegistroResponse = await res.json();
         console.info(data);
         // Mostrar animación de éxito
         setRegistroExitoso(true);
      } catch (err) {
         console.error('Error en registro:', err);
         if (err instanceof Error) {
            setErrMsg(err instanceof Error ? err.message : 'Error al registrar usuario');
            return;
         }

         let msg = JSON.stringify(err);
         //@ts-expect-error
         if (err && err.err) {
            //@ts-expect-error
            msg = err.err.description;
            const multiMsg = msg.split('|');
            if (multiMsg.length > 1) {
               msg = multiMsg[1];
            }
         }
         setErrMsg(msg);
      } finally {
         setLoadingRegistro(false);
      }
   }

   const loginUrl = `${SCRN_DS_LOG_IN.to}${window.location.search}`;
   if (registroExitoso) {
      return <DSSignUpExito registroExitoso loginUrl={loginUrl} />;
   }

   const isLoading = loadingPlanes || loadingRegistro;
   const existsFatalError = Boolean(errPlanesMsg);
   const isDisabled = isLoading || existsFatalError;

   return (
      <Container style={{height: '100%'}} component="main" maxWidth="sm">
         <Grid
            style={{height: '100%'}}
            container
            justifyContent="flex-end"
            alignItems="flex-end"
            spacing={2}
         >
            <Grid container item xs={12} justifyContent="center">
               <Card style={{maxWidth: 450, padding: 16, width: '100%'}}>
                  <CardMedia
                     component="img"
                     height="auto"
                     image="https://www.dscont.pe/dsServices/images/ds-iso9001-logo.png"
                     alt="Data Service Capacitacion SRL."
                     style={{
                        width: '90%',
                        padding: 2,
                        justifySelf: 'center',
                        margin: '0 auto'
                     }}
                  />
                  <CardContent>
                     <Collapse in={!registroExitoso}>
                        <Grid container spacing={2}>
                           <Grid item xs={12}>
                              <Typography
                                 gutterBottom
                                 variant="h5"
                                 align="center"
                                 color="textPrimary"
                              >
                                 Registro de Usuario
                              </Typography>
                           </Grid>

                           <Grid item xs={12}>
                              <TextField
                                 name="email"
                                 value={campos.email.valor}
                                 //@ts-expect-error
                                 onChange={handleOnChange}
                                 error={campos.email.error !== ''}
                                 helperText={campos.email.error}
                                 label="Email"
                                 fullWidth
                                 variant="standard"
                                 autoComplete="email"
                                 disabled={isDisabled}
                                 inputProps={{
                                    'aria-label': 'email',
                                    'data-testid': 'email-input'
                                 }}
                              />
                           </Grid>
                           {planes.length > 0 && (
                              <Grid item xs={12}>
                                 <FormControl
                                    fullWidth
                                    variant="standard"
                                    error={campos.plan.error !== ''}
                                 >
                                    <InputLabel id="plan-label">Plan</InputLabel>
                                    <Select
                                       labelId="plan-label"
                                       name="plan"
                                       value={campos.plan.valor}
                                       //@ts-expect-error
                                       onChange={handleOnChange}
                                       label="Plan"
                                       disabled={
                                          PRODUCTOS_DISPONIBLES_SIGNUP.length <= 1 || isDisabled
                                       }
                                    >
                                       <MenuItem value="">
                                          <em>Seleccione un plan</em>
                                       </MenuItem>
                                       {planes.map(currPlan => (
                                          <MenuItem
                                             key={currPlan.idPlan}
                                             value={currPlan.idPlan}
                                             title={currPlan.descripcion || undefined}
                                          >
                                             {currPlan.denominacion}
                                          </MenuItem>
                                       ))}
                                    </Select>
                                    {campos.plan.error && (
                                       <FormHelperText>{campos.plan.error}</FormHelperText>
                                    )}
                                 </FormControl>
                              </Grid>
                           )}
                           {errMsg && (
                              <Grid item xs={12}>
                                 <Typography
                                    variant="body2"
                                    align="center"
                                    color="error"
                                    component="p"
                                    role="alert"
                                 >
                                    {errMsg}
                                 </Typography>
                              </Grid>
                           )}

                           {loadingRegistro && (
                              <Grid container item xs={12} spacing={1}>
                                 <Grid item xs={12}>
                                    <Typography variant="body2" color="textSecondary">
                                       Procesando registro...
                                    </Typography>
                                 </Grid>
                                 <Grid item xs={12}>
                                    <LinearProgress aria-label="Procesando registro" />
                                 </Grid>
                              </Grid>
                           )}

                           <Grid container item xs={12} justifyContent="center">
                              <Button
                                 onClick={handleRegistro}
                                 type="submit"
                                 size="large"
                                 className={classes.signUpButton}
                                 disabled={isDisabled}
                                 fullWidth
                              >
                                 {loadingRegistro ? 'Registrando...' : 'Registrarse'}
                              </Button>
                           </Grid>

                           <Grid container item xs={12} justifyContent="center">
                              <Link
                                 component={RouterLink}
                                 to={loginUrl}
                                 variant="body2"
                                 underline="hover"
                              >
                                 ¿Ya tiene cuenta? Inicie sesión
                              </Link>
                           </Grid>
                        </Grid>
                     </Collapse>
                  </CardContent>
               </Card>
            </Grid>

            <Grid container item xs={12} spacing={2} justifyContent="center">
               <Grid item xs={12} sm={6}>
                  <Typography
                     variant="subtitle2"
                     align="center"
                     color="textSecondary"
                     component="p"
                  >
                     Copyright © {new Date().getFullYear()}{' '}
                     <Link target="_blank" href="https://www.dscont.pe/" underline="hover">
                        DsContSoftware
                     </Link>
                     . Todos los derechos reservados.
                  </Typography>
               </Grid>
            </Grid>
         </Grid>
      </Container>
   );
}

export default DSSignUp;
